import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { X, Sparkles, BookOpenCheck, IndianRupee, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'osc_offer_seen_v';

export default function OfferPopup({ onBook }) {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/offer`);
        if (cancelled || !data?.active) return;
        const version = data.updated_at || 'v1';
        const seen = sessionStorage.getItem(SESSION_KEY);
        if (seen === version) return;
        setOffer(data);
        setTimeout(() => setOpen(true), 900);
        sessionStorage.setItem(SESSION_KEY, version);
      } catch (e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Countdown tick
  useEffect(() => {
    if (!offer?.end_at) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [offer]);

  if (!offer) return null;

  const endMs = offer.end_at ? new Date(offer.end_at).getTime() : 0;
  const diff = endMs ? endMs - now : 0;
  const expired = endMs > 0 && diff <= 0;
  const timeParts = (() => {
    if (!endMs || diff <= 0) return null;
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { d, h, m, s };
  })();

  const handleBook = () => {
    setOpen(false);
    setTimeout(() => onBook?.('diamond'), 250);
  };

  const hasOriginal = !!(offer.original_price && String(offer.original_price).trim());
  const hasOffer = !!(offer.price && String(offer.price).trim());
  const discount = (() => {
    if (!hasOriginal || !hasOffer) return null;
    const o = parseInt(String(offer.original_price).replace(/[^\d]/g, ''), 10);
    const p = parseInt(String(offer.price).replace(/[^\d]/g, ''), 10);
    if (!o || !p || p >= o) return null;
    return Math.round(((o - p) / o) * 100);
  })();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0f0f16] border-purple-900/60 text-gray-200 max-w-md p-0 overflow-hidden">
        <button onClick={() => setOpen(false)} className="absolute right-3 top-3 z-10 h-8 w-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white/90"><X className="w-4 h-4" /></button>
        {offer.image_data_url ? (
          <div className="relative">
            <img src={offer.image_data_url} alt={offer.title || 'offer'} className="w-full h-48 md:h-56 object-cover" />
            <div className="absolute top-3 left-3 tag-pill"><Sparkles className="w-3 h-3" /> LATEST OFFER</div>
            {discount && (
              <div className="absolute top-3 right-14 tag-pill" style={{background:'linear-gradient(90deg,#16a34a,#22c55e)'}}>{discount}% OFF</div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f16] via-transparent to-transparent" />
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center" style={{background:'linear-gradient(135deg,#a855f7,#ec4899)'}}>
            <div className="tag-pill bg-white/20"><Sparkles className="w-3 h-3" /> LATEST OFFER</div>
          </div>
        )}
        <div className="p-5">
          {offer.title && (
            <h3 className="text-xl md:text-2xl font-extrabold text-white leading-tight">{offer.title}</h3>
          )}
          {offer.details && (
            <p className="mt-2 text-sm text-gray-300 whitespace-pre-line leading-relaxed">{offer.details}</p>
          )}
          {(hasOriginal || hasOffer) && (
            <div className="mt-4 flex items-end gap-3 flex-wrap">
              {hasOffer && (
                <div className="flex items-baseline">
                  <IndianRupee className="w-5 h-5 text-pink-400" />
                  <span className="gradient-text text-4xl font-extrabold leading-none">{offer.price}</span>
                  <span className="ml-1.5 text-[11px] text-gray-400">+ GST</span>
                </div>
              )}
              {hasOriginal && (
                <div className="flex items-center gap-1 text-red-400 line-through decoration-red-500 decoration-2 text-lg font-semibold pb-1">
                  <IndianRupee className="w-3.5 h-3.5" />{offer.original_price}
                </div>
              )}
            </div>
          )}
          {timeParts && (
            <div className="mt-4 rounded-xl border border-pink-500/40 bg-gradient-to-r from-purple-950/40 to-pink-950/40 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-pink-400 animate-pulse" />
                <span className="text-[11px] tracking-widest font-bold text-pink-400 uppercase">Offer Ends In</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                {[
                  { v: timeParts.d, l: 'DAYS' },
                  { v: timeParts.h, l: 'HRS' },
                  { v: timeParts.m, l: 'MIN' },
                  { v: timeParts.s, l: 'SEC' },
                ].map((t, i) => (
                  <div key={i} className="flex-1 rounded-lg bg-[#0a0a0f] border border-purple-900/60 py-2 text-center">
                    <div className="text-2xl font-extrabold gradient-text tabular-nums leading-none">{String(t.v).padStart(2, '0')}</div>
                    <div className="text-[10px] tracking-widest text-gray-400 mt-1">{t.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {expired && (
            <div className="mt-4 rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-center text-sm text-red-300 font-semibold">
              Offer Expired
            </div>
          )}
          <Button onClick={handleBook} disabled={expired} className={`rounded-full h-11 w-full mt-5 font-semibold ${expired ? 'bg-gray-700 text-gray-400' : 'gradient-btn'}`}>
            <BookOpenCheck className="w-4 h-4 mr-2" /> {offer.cta_label || 'હમણાં જ બુક કરો'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
